import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { usePolicyProductConfig } from '@folio/utils/usePolicyProductConfig';
import { useAssertedParams } from '../../../lib/utils';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  customerControllerGetCustomerPaymentMethodsQueryOptions,
  customerControllerGetCustomerQueryOptions,
  keyValueFieldsToObject,
  viewSpecificControllerListPolicyComplianceAlertsQueryOptions,
  Customer,
  PaymentMethodDtoWithIsPreferred,
  CustomerDto,
  GetCustomerPaymentMethodsOutput,
} from 'korint-sdk';
import { useAccessiblePayerCustomerIds } from '@folio/hooks';
import { buildRulePayers } from '@folio/utils/buildRulePayers';
import { FinalizeNewBusinessToDoList } from './FinalizeNewBusinessToDoList';
import { useFeatureFlaggedRules } from '../useFeatureFlaggedRules';
import { SendContractButton } from './SendContractButton';
import { CreateDerogationButton } from './CreateDerogationButton';
import { Group, Stack } from '@folio/components/ui/flex';

export function FinalizeNewBusinessLegacyCard() {
  const { t } = useTranslation();
  const { rules: configuredRules } = usePolicyProductConfig('finalizeNewBusinessModuleConfig');
  const rules = useFeatureFlaggedRules(configuredRules ?? []);

  const { policy, policyOnMain } = usePolicyOnDashboard();
  const { branchId } = useAssertedParams();

  const customers = useQueries({
    queries: policy.customers.map(customerId => ({
      ...customerControllerGetCustomerQueryOptions(customerId, { branchId }),
      select: (data: CustomerDto) => ({
        ...data,
        customFields: keyValueFieldsToObject(data.customFields ?? []),
      }),
    })),
  });

  const paymentMethods = useQueries({
    queries: policy.customers.map(customerId => ({
      ...customerControllerGetCustomerPaymentMethodsQueryOptions(customerId, { branchId }),
      select: (data: GetCustomerPaymentMethodsOutput) => data.data,
      enabled: policy.status !== 'POLICY_CREATED',
    })),
  });

  const { data: hasUnacknowledgedAlerts } = useQuery({
    ...viewSpecificControllerListPolicyComplianceAlertsQueryOptions(policy.id),
    select: data => data.data.some(({ acknowledged }) => !acknowledged),
  });

  const payerCustomerIds = useAccessiblePayerCustomerIds(policy);

  if (!customers[0].data || policyOnMain.status !== 'POLICY_CREATED') {
    return null;
  }

  const allCustomers = customers.map(customer => customer.data).filter(Boolean) as Customer[];
  const allPaymentMethods = paymentMethods
    .flatMap(paymentMethod => paymentMethod.data)
    .filter(Boolean) as PaymentMethodDtoWithIsPreferred[];

  const payers = buildRulePayers({
    customerIds: policy.customers,
    payerCustomerIds,
    customers,
    paymentMethods,
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{t('finalizeNewBusiness.action')}</CardTitle>
        <Group align="stretch" gap={0}>
          <SendContractButton
            rules={rules}
            customer={customers[0].data}
            customers={allCustomers}
            payers={payers}
            paymentMethods={allPaymentMethods}
            hasUnacknowledgedAlerts={Boolean(hasUnacknowledgedAlerts)}
          />
          <CreateDerogationButton
            action="QUOTE"
            rules={rules}
            customer={customers[0].data}
            customers={allCustomers}
            payers={payers}
            paymentMethods={allPaymentMethods}
            hasUnacknowledgedAlerts={Boolean(hasUnacknowledgedAlerts)}
          />
          <CreateDerogationButton
            action="CONFIRM"
            rules={rules}
            customer={customers[0].data}
            customers={allCustomers}
            payers={payers}
            paymentMethods={allPaymentMethods}
            hasUnacknowledgedAlerts={Boolean(hasUnacknowledgedAlerts)}
          />
        </Group>
      </CardHeader>
      <Stack asChild>
        <CardContent>
          <FinalizeNewBusinessToDoList
            rules={rules}
            customer={customers[0].data}
            customers={allCustomers}
            payers={payers}
            paymentMethods={allPaymentMethods}
            hasUnacknowledgedAlerts={Boolean(hasUnacknowledgedAlerts)}
          />
        </CardContent>
      </Stack>
    </Card>
  );
}
